/**
 * Midway Authentication — mwinit cookie + direct SSO fetch
 * @author Omid Zaeri (zaerio)
 *
 * Strategy:
 * 1. Try direct fetch to midway-auth.amazon.com/SSO with credentials (mwinit cookie)
 * 2. If that fails, try SentryJS browser extension (callAuthorize DOM events)
 * 3. Last resort: redirect to Midway login page
 */

const MIDWAY_SSO_URL = 'https://midway-auth.amazon.com/SSO';

function generateNonce() {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
}

/**
 * Attempt 1: Direct fetch to Midway SSO endpoint.
 * Works when the user has a valid Midway cookie (from mwinit or browser session).
 * The cookie is on .amazon.com domain so it's sent to midway-auth.amazon.com.
 */
async function fetchTokenDirect() {
  const params = new URLSearchParams({
    response_type: 'id_token',
    client_id: window.location.origin,
    redirect_uri: window.location.origin + '/',
    scope: 'openid',
    nonce: generateNonce(),
  });

  const res = await fetch(`${MIDWAY_SSO_URL}?${params.toString()}`, {
    credentials: 'include',
    redirect: 'follow',
  });

  if (res.ok) {
    const text = await res.text();
    // The response might be the token directly, or it might be HTML
    if (text && !text.startsWith('<') && !text.startsWith('{')) {
      return text.trim();
    }
    // Check if it's a JSON response with id_token
    try {
      const json = JSON.parse(text);
      if (json.id_token) return json.id_token;
    } catch (e) {
      // Not JSON, check if it looks like a JWT
      if (text.includes('.') && text.split('.').length === 3) {
        return text.trim();
      }
    }
  }

  // Check if the response URL contains an id_token fragment
  if (res.url && res.url.includes('id_token=')) {
    const url = new URL(res.url);
    const hash = url.hash || '';
    const hashParams = new URLSearchParams(hash.replace('#', ''));
    const token = hashParams.get('id_token');
    if (token) return token;
  }

  return null;
}

/**
 * Attempt 2: SentryJS browser extension approach.
 * The extension intercepts the `authorizeRequested` DOM event, performs the
 * Midway login flow, and returns the token via `authorizeReturnedSentryJS`.
 */
function callAuthorize(authorizeUrl) {
  if (!authorizeUrl) {
    return Promise.reject('Invalid authorize url');
  }

  let expectedHost;
  try {
    const url = new URL(authorizeUrl);
    const redirectUri = url.searchParams.get('redirect_uri');
    if (redirectUri) {
      expectedHost = new URL(decodeURI(redirectUri)).host;
    }
  } catch (e) {
    return Promise.reject('Invalid Authorize URL');
  }
  if (!expectedHost) {
    return Promise.reject('Could not parse Redirect URI');
  }

  const responsePromise = new Promise((resolve, reject) => {
    function handleEvent(event) {
      if (event.detail.host === expectedHost && event.detail.resultCode === 0) {
        window.removeEventListener('authorizeReturnedSentryJS', handleEvent);
        resolve(event.detail);
      } else if (event.detail.resultCode !== 0) {
        window.removeEventListener('authorizeReturnedSentryJS', handleEvent);
        reject(event.detail);
      }
    }
    window.addEventListener('authorizeReturnedSentryJS', handleEvent);
  });

  document.dispatchEvent(
    new CustomEvent('authorizeRequested', { detail: { authorizeUrl } })
  );

  return responsePromise;
}

function buildAuthorizeUrl() {
  const params = new URLSearchParams({
    response_type: 'id_token',
    client_id: window.location.origin,
    redirect_uri: window.location.origin + '/',
    scope: 'openid',
    nonce: generateNonce(),
  });
  return `${MIDWAY_SSO_URL}/redirect?${params.toString()}`;
}

async function fetchTokenViaSentryJS() {
  const authorizeUrl = buildAuthorizeUrl();
  const result = await Promise.race([
    callAuthorize(authorizeUrl),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('SentryJS timeout')), 5000)
    ),
  ]);
  if (result && result.id_token) return result.id_token;
  if (result && result.access_token) return result.access_token;
  return null;
}

/**
 * Check if there's an id_token in the URL hash (from a Midway redirect callback).
 */
function checkHashForToken() {
  const hash = window.location.hash;
  if (!hash) return null;
  const params = new URLSearchParams(hash.replace('#', ''));
  const token = params.get('id_token');
  if (token) {
    // Clean up the URL
    window.history.replaceState(null, '', window.location.pathname);
    return token;
  }
  return null;
}

/**
 * Get a Midway identity token. Tries multiple strategies in order.
 */
export async function getIdentityToken() {
  // Check if we already have a token from a redirect callback
  const hashToken = checkHashForToken();
  if (hashToken) return hashToken;

  // Strategy 1: Direct fetch with mwinit cookie
  try {
    const directToken = await fetchTokenDirect();
    if (directToken) {
      console.log('Midway auth: got token via direct SSO fetch');
      return directToken;
    }
  } catch (e) {
    console.warn('Direct Midway SSO fetch failed:', e);
  }

  // Strategy 2: SentryJS browser extension
  try {
    const sentryToken = await fetchTokenViaSentryJS();
    if (sentryToken) {
      console.log('Midway auth: got token via SentryJS');
      return sentryToken;
    }
  } catch (e) {
    console.warn('SentryJS auth failed:', e);
  }

  // Strategy 3: Redirect to Midway login
  console.log('Midway auth: redirecting to Midway login');
  const redirectUrl = buildAuthorizeUrl();
  window.location.assign(redirectUrl);
  return null;
}

export function extractAliasFromToken(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('Invalid JWT');
    const payload = parts[1];
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    const obj = JSON.parse(decoded);
    return obj.sub || obj.alias || obj.preferred_username || obj.email?.split('@')[0] || '';
  } catch (err) {
    console.error('Error decoding JWT:', err);
    return '';
  }
}
